import Phaser from "phaser";

import { PALETTE, UGC_CYAN } from "../config";
import { CARD_STACK_WIDTH } from "../scenes/board/geometry";
import type { BoardCard } from "../scenes/PlatformBoardScene";
import type { AuthorIdentityService } from "../services/AuthorIdentityService";
import type { GalleryLevel } from "../services/GalleryService";
import { RatingService } from "../services/RatingService";
import { hashString, makeSeededRng } from "../services/seededRng";

const CARD_H = 36;
// Author avatar — same framed-box renderer as the trade board's OfferCard.
const AVATAR_BOX_SIZE = 18;
const AVATAR_INNER_SIZE = 14;
const AVATAR_BOX_X = 4;
const AVATAR_BOX_Y = 15;
// Minimap thumbnail (bottom-right), styled like the runner's in-game strip.
const MM_W = 56;
const MM_H = 12;
const MM_X = CARD_STACK_WIDTH - MM_W - 4;
const MM_Y = 16;

/**
 * A single Weltkugel level card — a sibling of the trade board's OfferCard,
 * sized to the shared platform stack (200×36):
 *
 *   Level title                         ★ fun   ☠ diff
 *   [avatar] @author                    [minimap thumbnail]
 *
 * The avatar uses OfferCard's framed circle-cropped renderer; the minimap is a
 * deterministic procedural strip (seeded by the level) echoing the in-game
 * bottom HUD mini-map. Implements BoardCard so PlatformBoardScene tweens +
 * selects it.
 */
export class MapCard implements BoardCard {
  readonly container: Phaser.GameObjects.Container;
  private readonly bg: Phaser.GameObjects.Rectangle;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    level: GalleryLevel,
    identitySvc: AuthorIdentityService,
  ) {
    const hex = (c: number) => `#${c.toString(16).padStart(6, "0")}`;
    this.container = scene.add.container(x, y);

    this.bg = scene.add
      .rectangle(0, 0, CARD_STACK_WIDTH, CARD_H, PALETTE.levelCardBg, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE.levelCardEdge);

    const identity = identitySvc.forSeed(level.authorSeed);

    // Title (top-left, left-aligned) + rating (top-right).
    const title = scene.add
      .text(AVATAR_BOX_X, 5, this.truncate(level.title, 20), {
        fontFamily: "monospace",
        fontSize: "9px",
        color: hex(PALETTE.ink),
        fontStyle: "bold",
      })
      .setOrigin(0, 0);
    const agg = RatingService.simulatedAggregate(level.authorSeed);
    const rating = scene.add
      .text(CARD_STACK_WIDTH - 4, 6, `★ ${agg.fun.toFixed(1)}   ☠ ${agg.difficulty.toFixed(1)}`, {
        fontFamily: "monospace",
        fontSize: "8px",
        color: hex(PALETTE.geo),
      })
      .setOrigin(1, 0);

    // Avatar in a framed box (OfferCard renderer) + author name (bottom-left).
    const avatarBox = scene.add
      .rectangle(AVATAR_BOX_X, AVATAR_BOX_Y, AVATAR_BOX_SIZE, AVATAR_BOX_SIZE, PALETTE.authorBadgeBg, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE.shopCardEdge);
    const off = (AVATAR_BOX_SIZE - AVATAR_INNER_SIZE) / 2;
    const avatar = identitySvc.drawCircleAvatar(
      scene,
      AVATAR_BOX_X + off,
      AVATAR_BOX_Y + off,
      identity,
      AVATAR_INNER_SIZE,
    );
    const author = scene.add
      .text(AVATAR_BOX_X + AVATAR_BOX_SIZE + 4, AVATAR_BOX_Y + AVATAR_BOX_SIZE / 2, `@${identity.username}`, {
        fontFamily: "monospace",
        fontSize: "6px",
        color: hex(PALETTE.hudDim),
      })
      .setOrigin(0, 0.5);

    this.container.add([this.bg, title, rating, avatarBox, avatar, author]);
    this.drawMinimap(scene, MM_X, MM_Y, MM_W, MM_H, hashString(level.id));
  }

  setSelected(selected: boolean): void {
    this.bg.setStrokeStyle(selected ? 2 : 1, selected ? UGC_CYAN : PALETTE.levelCardEdge);
    this.bg.setFillStyle(PALETTE.levelCardBg, selected ? 1 : 0.85);
  }

  destroy(): void {
    this.container.destroy();
  }

  /**
   * Deterministic procedural mini-map thumbnail, styled like the runner's
   * bottom-HUD strip: a panel with a faint traversal trail, scattered
   * enemy/geo markers (top) and terrain dips (bottom), and a cyan exit gate
   * at the right end. Seeded by the level so it's stable per card.
   */
  private drawMinimap(scene: Phaser.Scene, lx: number, ly: number, w: number, h: number, seed: number): void {
    const rng = makeSeededRng(seed);
    const panel = scene.add
      .rectangle(lx, ly, w, h, PALETTE.hudPanel, 0.85)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE.hudDim);
    const g = scene.add.graphics();
    const innerW = w - 4;

    g.fillStyle(PALETTE.hudText, 0.22);
    g.fillRect(lx + 2, ly + h - 3, innerW, 1);

    const markers = 5 + rng.intRange(0, 4);
    for (let i = 0; i < markers; i++) {
      const mx = Math.round(lx + 2 + rng.next() * (innerW - 2));
      if (rng.chance(0.55)) {
        g.fillStyle(rng.chance(0.5) ? PALETTE.gegner : PALETTE.geo, 1);
        g.fillRect(mx, ly + 2, 2, 3);
      } else {
        g.fillStyle(0x000000, 1);
        g.fillRect(mx, ly + h - 4, 1, 2);
      }
    }

    g.fillStyle(UGC_CYAN, 1);
    g.fillRect(lx + w - 3, ly + 2, 2, h - 4);

    this.container.add([panel, g]);
  }

  private truncate(s: string, n: number): string {
    return s.length > n ? `${s.slice(0, n - 1)}…` : s;
  }
}
